import { Component, Inject, LOCALE_ID } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationTypes } from 'src/app/app.enums';
import { SetupItems } from 'src/app/models/setupItems';
import { ArtTypeService } from 'src/app/Services/art-type.service';
import { ArtistsService, ExternalInvitation, Artist } from 'src/app/Services/artists.service';
import { AuthConfig, AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { GalleriesService, GalleryAddress, GalleryItems } from 'src/app/Services/galleries.service';
import { Cities, Countries, RegistrationsService, States } from 'src/app/Services/registration.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { environment } from 'src/environments/environment';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-accept-invitation',
    templateUrl: './accept-invitation.component.html',
    styleUrls: ['./accept-invitation.component.css'],
    providers: [AuthService, ArtistsService, GalleriesService,],
    standalone: false
})
export class AcceptInvitationComponent {
  public showAdditionalInfo: boolean = false;
  public hidelable: boolean = true;
  public artTypes: SetupItems[] = [];
  public authConfig: AuthConfig = {};
  public invitationId!: string;
  public signupForm: Artist = {} as Artist;
  public currencyCode: string | undefined;
  public isExternalInviteFound: boolean = false;
  public invitedByUserFullName?: string;
  constructor(@Inject(LOCALE_ID) private locale: string,
    private router: Router,
    private route: ActivatedRoute,
    private jwtHelper: JwtHelperService,
    private ngxSpinnerService: NgxSpinnerService,
    private artistService: ArtistsService,
    private registrationsService: RegistrationsService,
    private authService: AuthService,
    private galleriesService: GalleriesService,
    private artTypeService: ArtTypeService,
    private notificationsService: NotificationsService,
    private transloco: TranslocoService) { }

  public ngOnInit(): void {
    if (this.authService.isAuthenticated()) {
      //if the user is already authenticated, redirect them to the home
      this.router.navigate(['/']);
    }

    this.authConfig = this.authService.getAuthConfig();
    this.route.params.subscribe((params) => {
      if (params['invitationId']) {
        this.invitationId = params['invitationId'];
        //try to get the external user (this will be called when the user was invited by someone else externally)
        this.getExternalUser();
      }
    });

    this.getArtTypes();
    this.fetchCountries()

  }


  private getExternalUser() {
    this.ngxSpinnerService.show();
    this.artistService.getExternalUser(this.invitationId).subscribe((externalInvitation: ExternalInvitation) => {
      if (externalInvitation && externalInvitation.IsAccepted == false) {
        //if there was an invitation found against the given invitationId which is not accepted yet
        console.log(externalInvitation)
        const [firstName, ...rest] = externalInvitation.Name?.trim().split(" ") || [];
        const lastName = rest.join(" ");
        this.signupForm.FirstName = firstName || '';
        this.signupForm.LastName = lastName || '';
        this.signupForm.Email = externalInvitation.Email;
        //this user was invited by the artist so it must have been a gallery, so set the account type as "Gallery" and vice versa
        this.signupForm.AccountType = externalInvitation.IsInvitedByArtist ? 'Gallery' : 'Artist';

        this.invitedByUserFullName = externalInvitation.InvitedByFullName;
        this.isExternalInviteFound = true;
      }
      this.ngxSpinnerService.hide();
    }, (error) => { console.error('Error fetching the invite details. Error details: ', error); this.ngxSpinnerService.hide(); });
  }

  private acceptExternalUserRequest(callback: (success: boolean) => void) {
    this.ngxSpinnerService.show();
    this.artistService.acceptExternalUserRequest(this.invitationId).subscribe(
      (response: ResponseObject) => {
        this.ngxSpinnerService.hide();
        callback(response.Success); // Ensure the `ResponseObject` has a `Success` property
      },
      (error) => {
        console.error('Error accepting external user request:', error);
        this.ngxSpinnerService.hide();
        callback(false); // Handle error case
      }
    );
  }

  private onAfterRegistration() {
    //check if the angular application is running in an iframe or not
    if (window.self === window.top) {
      //not an iframe so proceed with the normal reload
      window.location.reload();
    } else {
      //user is viewing the site in the iframe so navigate him properly to the portal site
      if (environment.production && window.parent) {
        window.parent.location.href = environment.portalUrl;
      }
    }
  }
  private login(userName: string, password: string) {
    this.ngxSpinnerService.show();
    this.authService.login({ Username: userName, Password: password }).subscribe((loginResponse: ResponseObject) => {
      if (loginResponse.Success) {
        this.onAfterRegistration();
      }
      this.ngxSpinnerService.hide();
    }, error => { console.error(error); this.ngxSpinnerService.hide(); });
  }

  private saveArtist() {
    var artist = new Artist();
    // artist.Name = this.signupForm.Name;
    artist.FirstName = this.signupForm.FirstName
    artist.LastName = this.signupForm.LastName
    artist.Email = this.signupForm.Email;
    artist.Password = this.signupForm.Password;
    artist.ConfirmPassword = this.signupForm.ConfirmPassword;
    artist.BusinessName = this.signupForm.BusinessName;

    this.ngxSpinnerService.show();
    this.artistService.saveArtist(artist).subscribe((response) => {
      this.ngxSpinnerService.hide();
      if (response.Success) {
        //login now
        this.ngxSpinnerService.show();
        this.authService.login({ Username: artist.Email, Password: artist.Password }).subscribe((loginResponse: ResponseObject) => {
          if (loginResponse.Success) {
            //accrpt the invite now
            localStorage.setItem('isNewUser', 'true');
            this.acceptExternalUserRequest((success: boolean) => {
              //reload the window now after the invite got accepted
              window.location.reload();
            });
          }
          this.ngxSpinnerService.hide();
        }, error => { console.error(error); this.ngxSpinnerService.hide(); });
      } else { this.notificationsService.showNotification(this.transloco.translate('acceptInvitation.errorTitle'), response.ResponseMessage, NotificationTypes.Error); }
    }, (error) => { console.error(error); this.ngxSpinnerService.hide(); });
  }

  private saveGallery() {
    // Create a new object with only the required properties
    const galleryData: GalleryItems = {
      // Name: this.signupForm.Name,
      FirstName: this.signupForm.FirstName,
      UserName: this.signupForm.UserName,
      LastName: this.signupForm.LastName,
      Email: this.signupForm.Email,
      Password: this.signupForm.Password,
      ConfirmPassword: this.signupForm.ConfirmPassword,
      BusinessName: this.signupForm.BusinessName,
      GalleryAddresses: []
    };

    this.ngxSpinnerService.show();
    this.galleriesService.saveGallery(galleryData).subscribe((response: ResponseObject) => {
      this.ngxSpinnerService.hide();
      if (response.Success) {
        //login now
        this.ngxSpinnerService.show();
        this.authService.login({ Username: galleryData.Email, Password: galleryData.Password }).subscribe((loginResponse: ResponseObject) => {
          if (loginResponse.Success) {
            localStorage.setItem('isNewUser', 'true');
            this.acceptExternalUserRequest((success: boolean) => {
              window.location.reload();
            });
          }
          this.ngxSpinnerService.hide();
        }, error => { console.error(error); this.ngxSpinnerService.hide(); });
      } else { this.notificationsService.showNotification(this.transloco.translate('acceptInvitation.errorTitle'), response.ResponseMessage, NotificationTypes.Error); }
    }, (error) => { console.error(error); this.ngxSpinnerService.hide(); });
  }

  public isPasswordMismatch(): boolean {
    return this.signupForm.Password !== this.signupForm.ConfirmPassword;
  }

  public signUp() {
    if (this.signupForm.AccountType === 'Artist') {
      this.saveArtist();
    } else if (this.signupForm.AccountType === 'Gallery') {
      this.saveGallery();
    }
  }

  private getArtTypes() {
    this.artTypeService.fetch().subscribe((artTypes: SetupItems[]) => {
      this.artTypes = artTypes;
    }, (error) => { console.error('Error fetching art types:', error); });
  }

  // Correct Properties
  public selectedCountryId!: number;
  public selectedStateId!: number;
  public selectedCityId!: number;

  // Add helper fields to store selected names
  public selectedCountryName!: string;
  public selectedStateName!: string;
  public selectedCityName!: string;


  public countries: Countries[] = [];
  public states: States[] = [];
  public cities: Cities[] = [];


  private fetchCountries(): void {
    this.registrationsService.getAllCountries().subscribe(
      (countries: Countries[]) => {
        this.countries = countries;
        console.log('Fetched countries:', this.countries);
      },
      (error) => {
        console.error('Error fetching countries:', error);
      }
    );
  }


  // Fetch states based on selected country
  private fetchStates(countryId: number): void {
    this.registrationsService.getAllStates(countryId).subscribe(
      (states: States[]) => {
        this.states = states;
        console.log('Fetched states:', this.states);
      },
      (error) => {
        console.error('Error fetching states:', error);
      }
    );
  }

  // Fetch cities based on selected state
  private fetchCities(stateId: number): void {
    this.registrationsService.getAllCities(stateId).subscribe(
      (cities: Cities[]) => {
        this.cities = cities;
        console.log('Fetched cities:', this.cities);
      },
      (error) => {
        console.error('Error fetching cities:', error);
      }
    );
  }
  // When user selects a country
  public onCountryChange(event: any): void {
    const selectedId = +event.target.value;
    this.selectedCountryId = selectedId;

    // Find selected Country object
    const selectedCountryObj = this.countries.find(c => c.id === selectedId);
    this.selectedCountryName = selectedCountryObj ? selectedCountryObj.name : '';

    // Console for debugging
    console.log('Selected Country ID:', this.selectedCountryId);
    console.log('Selected Country Name:', this.selectedCountryName);

    // Reset State & City selections
    this.selectedStateId = undefined!;
    this.selectedCityId = undefined!;
    this.states = [];
    this.cities = [];

    // Fetch states if country is selected
    if (this.selectedCountryId) {
      this.fetchStates(this.selectedCountryId);
    }
  }

  // When user selects a state
  public onStateChange(event: any): void {
    const selectedId = +event.target.value;
    this.selectedStateId = selectedId;

    // Find selected State object
    const selectedStateObj = this.states.find(s => s.id === selectedId);
    this.selectedStateName = selectedStateObj ? selectedStateObj.name : '';

    // Console for debugging
    console.log('Selected State ID:', this.selectedStateId);
    console.log('Selected State Name:', this.selectedStateName);

    // Reset City selection
    this.selectedCityId = undefined!;
    this.cities = [];

    // Fetch cities if state is selected
    if (this.selectedStateId) {
      this.fetchCities(this.selectedStateId);
    }
  }

  // When user selects a city
  public onCityChange(event: any): void {
    const selectedId = +event.target.value;
    this.selectedCityId = selectedId;

    // Find selected City object
    const selectedCityObj = this.cities.find(c => c.id === selectedId);
    this.selectedCityName = selectedCityObj ? selectedCityObj.name : '';

    // Console for debugging
    console.log('Selected City ID:', this.selectedCityId);
    console.log('Selected City Name:', this.selectedCityName);
  }


  public register() {
    this.signupForm.Country = this.selectedCountryName!;
    this.signupForm.State = this.selectedStateName;
    this.signupForm.City = this.selectedCityName;
    console.log('Form Data:', this.signupForm);
    if (this.signupForm.AccountType === 'Artist') {
      this.saveArtist();
    } else if (this.signupForm.AccountType === 'Gallery') {
      this.saveGallery();
    }
  }
}

